﻿import { useRef, useState, useEffect } from 'react'
import { log } from '@/utils/logger'
import { Loader2, FileText, CloudUpload, ScanText, Image as ImageIcon, File as FileIcon } from 'lucide-react'
import { cn } from '@/utils/cn'
import { useValuationStore } from '@/store/valuationStore'
import { useUiStore } from '@/store/uiStore'
import { useAuthStore } from '@/store/authStore'
import { useRefStore } from '@/store/refStore'
import { REF_KEYS } from '@/data'
import { useAttributeStore } from '@/store/attributeStore'
import { mapAttributesToFields } from '@/utils/attributeMapper'
import { normaliseToKey } from '@/data/refCategories'
import { processOcrImage, normaliseOcrValue } from '@/services/ocrService'
import { decodeVin } from '@/services/vinService'
import { FIELD_VALIDATORS } from '@/utils/fieldValidators'
import { mapResponseToForm } from '@/utils/fieldResponseMapper'
import { ALL_SECTIONS, FIELD_LABEL_MAP, FIELD_TYPE_MAP } from '@/data/allSections'
import type { FieldResponsePayload, MappedField } from '@/types'
import { AiProcessModal, type AiProcessField } from '@/components/ui/AiProcessModel'
import { PhotoLightbox } from '@/components/ui/PhotoLightbox'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { getMessage } from '@/utils/messages'
import { getPhotoLimit } from '@/config/photoLimits'
import { computeFileHash } from '@/utils/fileHash'

const SECTION_ID = 'form-recognizer'

const ALLOWED_EXT = new Set(['.pdf'])

const ISO_DATE = /^(?:\d{4}-\d{2}-\d{2}|\d{1,2}[/-]\d{1,2}[/-]\d{2,4})$/

interface FileTypeStyle {
  bgColor: string
  borderColor: string
  iconColor: string
  badgeBg: string
  label: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Icon: React.ComponentType<any>
}

function getFileTypeStyle(mimeType?: string, name?: string): FileTypeStyle {
  const ext = (name?.split('.').pop() ?? '').toUpperCase()
  if (mimeType === 'application/pdf' || ext === 'PDF') {
    return { bgColor: '#EFF6FF', borderColor: '#93C5FD', iconColor: 'text-blue-500', badgeBg: '#3B82F6', label: 'PDF', Icon: FileText }
  }
  if (mimeType?.startsWith('image/')) {
    return { bgColor: '#F0FDF4', borderColor: '#86EFAC', iconColor: 'text-green-600', badgeBg: '#16A34A', label: ext || 'IMG', Icon: ImageIcon }
  }
  return { bgColor: 'var(--surface2)', borderColor: 'var(--border)', iconColor: 'text-[var(--text-muted)]', badgeBg: '#64748B', label: ext || 'FILE', Icon: FileIcon }
}

export function FormRecognizer() {
  const { photos, addPhoto, removePhoto, removePhotoLocal, applyAIFields, setOcrResult, setPhotoResult, clearSectionOcrData, clearSpecificFields, promoteFieldConfidence, updatePhotoServerNames, sectionStates, toggleSection, photoResults, ocrResults } = useValuationStore()
  const caseId = useValuationStore((s) => s.caseId) ?? ''
  const isSubmitted = useValuationStore((s) => s.status === 'submitted')
  const { showToast } = useUiStore()

  const cardRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const pendingPhotoIdRef = useRef<string | undefined>(undefined)

  const isExpanded = sectionStates['valuation-report']?.expanded ?? true
  const [isDragOver, setIsDragOver] = useState(false)
  const [ocrLoading, setOcrLoading] = useState(false)
  const [ocrMappedFields, setOcrMappedFields] = useState<MappedField[]>([])
  const [ocrFields, setOcrFields] = useState<AiProcessField[]>([])
  const [ocrModalOpen, setOcrModalOpen] = useState(false)
  const [vinMappedFields, setVinMappedFields] = useState<MappedField[]>([])
  const [vinModalOpen, setVinModalOpen] = useState(false)
  const [vinDecodeConfirmOpen, setVinDecodeConfirmOpen] = useState(false)
  const [vinDecodeSnapshot, setVinDecodeSnapshot] = useState<MappedField[]>([])
  const [vinDecoding, setVinDecoding] = useState(false)
  const [unsupportedExt, setUnsupportedExt] = useState<string | null>(null)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [removeModalOpen, setRemoveModalOpen] = useState(false)
  const [removeModalPhotoId, setRemoveModalPhotoId] = useState<string | null>(null)
  const [removeModalFields, setRemoveModalFields] = useState<AiProcessField[]>([])
  // Retains the original MappedField[] (with fieldKey) so clearSpecificFields uses the
  // correct field key and never touches vehicleType / use / type values.
  const [removeModalMappedFields, setRemoveModalMappedFields] = useState<MappedField[]>([])
  const [removeModalHasRetainedFields, setRemoveModalHasRetainedFields] = useState(false)
  const [removeModalConfidenceUpdates, setRemoveModalConfidenceUpdates] = useState<Array<{ sectionId: string; fieldKey: string; confidence: number }>>([])
  const [selectedVehicleType, setSelectedVehicleType] = useState('')
  const [vtMismatchInfo, setVtMismatchInfo] = useState<{ currentValue: string; responseValue: string } | null>(null)
  const [viewPhotoId, setViewPhotoId] = useState<string | null>(null)
  const [viewSummaryOpen, setViewSummaryOpen] = useState(false)

  const valuationReportFields = ocrResults[SECTION_ID] ?? []

  const sectionPhotos = photos.filter((p) => p.sectionId === SECTION_ID)

  const checkPhotoLimit = (): boolean => {
    const limit = getPhotoLimit(SECTION_ID)
    const current = useValuationStore.getState().photos.filter((p) => p.sectionId === SECTION_ID).length
    if (current >= limit) {
      showToast(getMessage('photo_strip.error_limit_reached', { limit: String(limit) }), 'error')
      return false
    }
    return true
  }

  const resolveHashOrRejectDuplicate = async (blob: Blob): Promise<string | null> => {
    const hash = await computeFileHash(blob)
    const existing = useValuationStore.getState().photos.filter((p) => p.sectionId === SECTION_ID)
    if (existing.some((p) => p.contentHash === hash)) {
      showToast(getMessage('photo_strip.error_duplicate'), 'error')
      return null
    }
    return hash
  }

  // Scroll into view when expanded — same pattern as SectionCard
  useEffect(() => {
    if (isExpanded && cardRef.current) {
      const timer = setTimeout(() => {
        cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 50)
      return () => clearTimeout(timer)
    }
  }, [isExpanded])

  // Prevent default drag behaviour on the whole page so drops land only in the zone
  useEffect(() => {
    const prevent = (e: DragEvent) => e.preventDefault()
    document.addEventListener('dragover', prevent)
    document.addEventListener('drop', prevent)
    return () => {
      document.removeEventListener('dragover', prevent)
      document.removeEventListener('drop', prevent)
    }
  }, [])


  // Fields that must never be auto-cleared when a photo is removed —
  // they drive section visibility and dropdown population.
  const REMOVE_EXCLUDED_FIELDS = new Set(['vehicleType', 'use', 'type'])

  // Opens the remove-image modal: reads this photo's detected fields from photoResults,
  // keeps only those whose form value still matches the AI/OCR-detected value
  // (not manually overridden), and excludes protected fields.
  const handleRemoveClick = (photoId: string) => {
    // Always read from getState() so fields and allResults share the same snapshot.
    const { formValues: fv, photoResults: allResults, photos: allPhotos } = useValuationStore.getState()
    const fields = allResults[photoId] ?? []
    // Use all photos across all sections — a backing image may live in a different section.
    const remainingPhotos = allPhotos.filter(p => p.id !== photoId)

    const matchedFields = fields.filter(
      (f) =>
        !REMOVE_EXCLUDED_FIELDS.has(f.fieldKey) &&
        fv[f.sectionId]?.[f.fieldKey] === f.value
    )

    // Fields backed by at least one remaining photo that detected the same fieldKey
    // (value need not be identical — if another image covers this field, we retain it silently).
    const backedByOther = matchedFields.filter(f =>
      remainingPhotos.some(p =>
        (allResults[p.id] ?? []).some(of => of.fieldKey === f.fieldKey)
      )
    )

    const fieldsToShow = matchedFields.filter(f => !backedByOther.includes(f))

    const confidenceUpdates = backedByOther.map(f => {
      const best = remainingPhotos.reduce((max, p) => {
        const match = (allResults[p.id] ?? []).find(of => of.fieldKey === f.fieldKey)
        return match && (match.confidence ?? 0) > max ? (match.confidence ?? 0) : max
      }, 0)
      return { sectionId: f.sectionId, fieldKey: f.fieldKey, confidence: best }
    }).filter(u => u.confidence > 0)

    setRemoveModalFields(toModalFields(fieldsToShow))
    setRemoveModalMappedFields(fieldsToShow)
    setRemoveModalHasRetainedFields(backedByOther.length > 0)
    setRemoveModalConfidenceUpdates(confidenceUpdates)
    setRemoveModalPhotoId(photoId)
    setRemoveModalOpen(true)
  }

  const isoToDisplay = (v: string) => {
    const m = v?.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    return m ? `${m[2]}/${m[3]}/${m[1]}` : v
  }

  const expandBasicIfNoneOpen = () => {
    const { sectionStates, expandSection } = useValuationStore.getState()
    if (!Object.values(sectionStates).some((s) => s.expanded)) {
      expandSection('basic-information')
    }
  }

  // Builds sections for mapResponseToForm with live ref-store options injected into
  // use/type fields so AI-detected values can be resolved against real dropdown options.
  // Falls back to form values for body-information (loads from attrCache when available).
  const buildSectionsForMapping = () => {
    const { formValues: fv } = useValuationStore.getState()
    const vehicleType = fv['basic-information']?.['vehicleType'] ?? ''
    const useValue  = fv['standard-equipment']?.['use']  ?? ''
    const typeValue = fv['standard-equipment']?.['type'] ?? ''
    const attrCache = useAttributeStore.getState().cache
    const refLabels = useRefStore.getState().getLabels

    let bodyFields: ReturnType<typeof mapAttributesToFields> = []
    if (vehicleType === 'Truck/Tractor' && useValue && useValue !== 'Select') {
      const attrs = attrCache[`TRUCK_BODY_${normaliseToKey(useValue)}`]
      if (attrs?.length) bodyFields = mapAttributesToFields(attrs)
    } else if (vehicleType === 'Trailer' && typeValue && typeValue !== 'Select') {
      const attrs = attrCache[`TRAILER_BODY_${normaliseToKey(typeValue)}`]
      if (attrs?.length) bodyFields = mapAttributesToFields(attrs)
    }

    const truckUseOptions   = refLabels(REF_KEYS.TRUCK_USE)   ?? []
    const truckTypeOptions  = refLabels(REF_KEYS.TRUCK_TYPE)  ?? []
    const trailerTypeOptions = refLabels(REF_KEYS.TRAILER_TYPE) ?? []

    return ALL_SECTIONS.map((s) => {
      if (s.id === 'body-information') return { ...s, fields: bodyFields }
      if (s.id === 'standard-equipment') {
        return {
          ...s,
          fields: s.fields.map((f) => {
            if (f.id === 'use'  && truckUseOptions.length > 0)   return { ...f, options: truckUseOptions }
            if (f.id === 'truck_type' && truckTypeOptions.length > 0) return { ...f, options: truckTypeOptions }
            if (f.id === 'type' && trailerTypeOptions.length > 0) return { ...f, options: trailerTypeOptions }
            return f
          }),
        }
      }
      return s
    })
  }

  const toModalFields = (fields: MappedField[], sourceOverride?: string) =>
    fields.map((mf) => ({
      label: mf.label || FIELD_LABEL_MAP[mf.fieldKey] || mf.fieldKey,
      aiValue: isoToDisplay(mf.value),
      value: isoToDisplay(useValuationStore.getState().formValues[mf.sectionId]?.[mf.fieldKey] || 'NA'),
      source: sourceOverride ?? mf.source,
      confidence: mf.confidence,
      sectionId: mf.sectionId,
    }))

  const handleVinDecode = async (appliedFields: MappedField[]) => {
    const vinField = appliedFields.find(f => f.fieldKey === 'vin')
    if (!vinField?.value) return

    const vinError = FIELD_VALIDATORS.vin(vinField.value)
    if (vinError) { showToast(vinError); return }

    setOcrLoading(true)
    try {
      const response = await decodeVin(vinField.value)
      const vinSections = (response?.data as { sections?: { section: string; fields: Record<string, { value: unknown; confidence: number; source: string }> }[] })?.sections ?? []

      const payload: FieldResponsePayload = {
        sections: Object.fromEntries(
          vinSections.map(({ section, fields }) => [
            section,
            {
              section,
              fields: Object.fromEntries(
                Object.entries(fields).map(([key, entry]) => [
                  key,
                  { value: String(entry.value ?? ''), confidence: entry.confidence ?? 1, source: entry.source },
                ])
              ),
            },
          ])
        ),
      }

      const { mappedFields: allVinMappedFields } = mapResponseToForm(payload, buildSectionsForMapping())
      const mappedFields = allVinMappedFields.filter(f => f.value && f.value.trim() !== '' && f.value.trim() !== 'No Data Found')
      if (mappedFields.length > 0) {
        setVinMappedFields(mappedFields)
        setVinModalOpen(true)
      }
    } catch (err) {
      log.error('VIN decode failed', err instanceof Error ? err : new Error(String(err)))
      const apiMsg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      showToast(apiMsg ?? getMessage('vin.error.decode_failed'))
    } finally {
      setOcrLoading(false)
    }
  }

  // Core OCR processing — shared by file picker and drag-and-drop
  const processFile = async (file: File, contentHash: string) => {
    const existingPhotoIds = new Set(useValuationStore.getState().photos.map((p) => p.id))

    addPhoto({ sectionId: SECTION_ID, blob: file, type: 'upload', name: file.name, mimeType: file.type, contentHash })

    const addedPhotoId = useValuationStore.getState().photos.find((p) => !existingPhotoIds.has(p.id))?.id

    setOcrLoading(true)

    try {
      const frUserId = String(useAuthStore.getState().user?.userId ?? '')
      const response = await processOcrImage(file, frUserId, caseId, SECTION_ID)

      if (response?.success) {
        await updatePhotoServerNames(frUserId, caseId, SECTION_ID, 'form-recognizer')
      }

      const ocrSections = response?.data?.sections ?? []
      const payload: FieldResponsePayload = {
        sections: Object.fromEntries(
          ocrSections.map(({ section, fields }) => [
            section,
            {
              section,
              fields: Object.fromEntries(
                Object.entries(fields).map(([key, entry]) => [
                  key,
                  { value: normaliseOcrValue(String(entry.value ?? '')), confidence: entry.confidence ?? 1, source: entry.source },
                ])
              ),
            },
          ])
        ),
      }

      const { mappedFields: allMappedFields } = mapResponseToForm(payload, buildSectionsForMapping())
      const mappedFields = allMappedFields
        .map((f) => ({ ...f, source: getMessage('ocr.source_label') }))
        .map((f) => f.fieldKey === 'vin' ? { ...f, value: f.value.replace(/\s+/g, '').toUpperCase() } : f)
        // Confidence gating already happened server-side (ocr.mapper.ts — per-field
        // validation.minConfidence), so this list only ever contains fields that already
        // cleared the appropriate threshold. No re-check here.
        .filter((f) =>
          !(FIELD_TYPE_MAP[f.fieldKey] === 'date' && !ISO_DATE.test(f.value)) &&
          !(f.fieldKey === 'vin' && !!FIELD_VALIDATORS.vin(f.value))
        )

      // Track the photo from this point so the mismatch dialog's onConfirm can always
      // reach it — even in the upload-time guard path where we return early.
      pendingPhotoIdRef.current = addedPhotoId

      // Vehicle Type mismatch guard — block upload if form already has a different vehicleType
      const vtDetected = mappedFields.find(f => f.fieldKey === 'vehicleType')
      const vtFormCurrent = (useValuationStore.getState().formValues['basic-information'] as Record<string, string>)?.['vehicleType'] ?? ''
      if (
        vtDetected?.value &&
        vtFormCurrent &&
        vtFormCurrent !== 'Select' &&
        vtDetected.value.toLowerCase() !== vtFormCurrent.toLowerCase()
      ) {
        if (addedPhotoId) {
          await removePhoto(addedPhotoId, { forceDelete: true })
          // Guarantee local removal — removePhoto returns early without touching local
          // state when the blob API call fails, so call removePhotoLocal as a fallback.
          removePhotoLocal(addedPhotoId)
        }
        setVtMismatchInfo({ currentValue: vtFormCurrent, responseValue: vtDetected.value })
        return
      }

      // Exclude vehicleType from the table — it gets its own editable row
      const tableFields = mappedFields.filter(f => f.fieldKey !== 'vehicleType')

      if (tableFields.length === 0) {
        // No usable fields (including the case where only vehicleType was detected)
        // → treat as empty result, do not show vehicleType selector
        setSelectedVehicleType('')
        setOcrFields([])
        setOcrMappedFields([])
      } else {
        // Other fields exist — add the mandatory vehicleType selector as the first row
        const refVehicleTypes = useRefStore.getState().getLabels(REF_KEYS.VEHICLE_TYPE) ?? []
        const vtOptions = refVehicleTypes.filter(o => !o.startsWith('Select'))
        const vtOptionsFinal = vtOptions.length > 0 ? vtOptions : ['Truck/Tractor', 'Trailer']

        // Pre-select only when OCR detected an exact-matching vehicle type; otherwise leave blank
        const vtDetected = mappedFields.find(f => f.fieldKey === 'vehicleType')
        const initVehicleType = vtDetected
          ? vtOptionsFinal.find(opt => opt.toLowerCase() === vtDetected.value.toLowerCase()) ?? ''
          : ''
        setSelectedVehicleType(initVehicleType)

        const vtFormCurrent = (useValuationStore.getState().formValues['basic-information'] as Record<string, string>)?.['vehicleType'] ?? ''
        const vtAiField: AiProcessField = {
          label: FIELD_LABEL_MAP['vehicleType'] || 'Vehicle Type',
          aiValue: initVehicleType,
          value: vtFormCurrent || 'NA',
          sectionId: 'basic-information',
          required: true,
          editOptions: vtOptionsFinal,
          onEditChange: (newValue) => {
            // Always sync selectedVehicleType so the Apply-time guard can compare it
            // against the form value, even when the user clicks Apply before the
            // mismatch dialog renders (race condition).
            setSelectedVehicleType(newValue)
            const currentVt = (useValuationStore.getState().formValues['basic-information'] as Record<string, string>)?.['vehicleType'] ?? ''
            if (
              newValue &&
              currentVt &&
              currentVt !== 'Select' &&
              newValue.toLowerCase() !== currentVt.toLowerCase()
            ) {
              setVtMismatchInfo({ currentValue: currentVt, responseValue: newValue })
            }
          },
        }

        setOcrMappedFields(tableFields)
        setOcrFields([vtAiField, ...toModalFields(tableFields)])
      }
      setOcrModalOpen(true)
    } catch (err) {
      log.error('Form Recognizer error', err instanceof Error ? err : new Error(String(err)))
      if (addedPhotoId) removePhoto(addedPhotoId)
      showToast(getMessage('ocr.error.scan_failed', { filename: file.name }), 'error')
    } finally {
      setOcrLoading(false)
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    if (!checkPhotoLimit()) return
    const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase()
    if (!ALLOWED_EXT.has(ext)) { setUnsupportedExt(ext); return }
    setOcrLoading(true)
    const contentHash = await resolveHashOrRejectDuplicate(file)
    if (!contentHash) { setOcrLoading(false); return }
    await processFile(file, contentHash)
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    if (ocrLoading) return
    if (!checkPhotoLimit()) return
    const file = e.dataTransfer.files?.[0]
    if (!file) return
    const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase()
    if (!ALLOWED_EXT.has(ext)) { setUnsupportedExt(ext); return }
    setOcrLoading(true)
    const contentHash = await resolveHashOrRejectDuplicate(file)
    if (!contentHash) { setOcrLoading(false); return }
    await processFile(file, contentHash)
  }

  return (
    <>
      {/* ── Claim Intake Documents section card ─────────────────────── */}
      <div
        ref={cardRef}
        data-tour="valuation-report-upload"
        style={{ scrollMarginTop: '72px' }}
        className={cn(
          'bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)]',
          'mb-3 overflow-hidden transition-[border-color] duration-200',
          isExpanded && 'border-[var(--border-active)]'
        )}
      >
        {/* Collapsible header */}
        <div
          onClick={() => toggleSection('valuation-report')}
          className={cn(
            'flex items-center gap-[14px] px-5 py-[15px]',
            'cursor-pointer select-none transition-[background] duration-150',
            'hover:bg-[rgba(0,0,0,0.02)] section-head'
          )}
        >
          {/* Section icon */}
          <div
            className={cn(
              'w-9 h-9 rounded-[9px] flex items-center justify-center flex-shrink-0',
              'border transition-all duration-200',
              isExpanded && 'bg-[var(--accent)] border-[var(--accent)] text-white'
            )}
            style={!isExpanded ? { backgroundColor: 'rgba(61,142,240,0.12)', borderColor: 'rgba(61,142,240,0.4)', color: 'var(--accent)' } : undefined}
          >
            <ScanText size={18} strokeWidth={2} />
          </div>

          {/* Title */}
          <span
            className="text-[14px] font-bold flex-1 text-[var(--text)]"
            style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
          >
            {getMessage('form_recognizer.section_title')}
          </span>


          {/* Chevron */}
          <span
            className={cn(
              'text-[var(--text-muted)] text-[20px] flex-shrink-0 self-start mt-[3px]',
              'transition-transform duration-[280ms]',
              isExpanded && 'rotate-180'
            )}
          >
            ▾
          </span>
        </div>

        {/* Expanded body */}
        {isExpanded && (
          <div className="animate-slide-down border-t border-[var(--border)] p-3 sm:p-4">
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">

              {/* Upload / drop zone */}
              <div
                onDrop={isSubmitted ? undefined : handleDrop}
                onDragOver={isSubmitted ? undefined : (e) => { e.preventDefault(); setIsDragOver(true) }}
                onDragEnter={isSubmitted ? undefined : (e) => { e.preventDefault(); setIsDragOver(true) }}
                onDragLeave={isSubmitted ? undefined : () => setIsDragOver(false)}
                onClick={() => !isSubmitted && !ocrLoading && checkPhotoLimit() && fileInputRef.current?.click()}
                title={getMessage('form_recognizer.tooltip_upload')}
                className={cn(
                  'flex flex-col items-center justify-center gap-2 p-5 min-h-[110px]',
                  'border-2 border-dashed rounded-[var(--radius)]',
                  'select-none transition-all duration-[180ms]',
                  isSubmitted
                    ? 'border-[var(--border)] opacity-50 cursor-not-allowed pointer-events-none'
                    : isDragOver
                      ? 'border-[var(--accent)] bg-[rgba(61,142,240,0.08)] cursor-pointer'
                      : 'border-[var(--border)] hover:border-[var(--accent)] hover:bg-[rgba(61,142,240,0.04)] cursor-pointer',
                  ocrLoading && 'opacity-50 pointer-events-none'
                )}
              >
                <CloudUpload
                  className={cn(
                    'w-7 h-7 transition-colors duration-[180ms]',
                    isDragOver ? 'text-[var(--accent)]' : 'text-[var(--text-muted)]'
                  )}
                  strokeWidth={1.5}
                />
                <div className="text-center">
                  <p className="text-[12px] font-bold text-[var(--text)] uppercase tracking-wide">
                    {getMessage('form_recognizer.upload_label')}
                  </p>
                  <p className="text-[11px] text-[var(--text-muted)] mt-[2px]">
                    {getMessage('form_recognizer.upload_hint')}
                  </p>
                  {(() => {
                    const limit = getPhotoLimit(SECTION_ID)
                    const current = sectionPhotos.length
                    const isAtLimit = current >= limit
                    return (
                      <span className="inline-flex items-center mt-[6px] px-[7px] py-[2px] rounded-full text-[10px] font-medium bg-[var(--surface2)] border border-[var(--border)] text-[var(--text-dim)]">
                        {isAtLimit
                          ? getMessage('photo_strip.upload_limit_reached', { current: String(current), limit: String(limit) })
                          : getMessage('photo_strip.upload_count', { current: String(current), limit: String(limit) })}
                      </span>
                    )
                  })()}
                </div>
              </div>

              {/* Recent uploads panel — always visible */}
              <div className="flex flex-col gap-2">
                {/* <p className="text-[10px] font-bold uppercase tracking-[0.6px] text-[var(--text-dim)]">
                  {getMessage('form_recognizer.recent_uploads_label')}
                </p> */}

                {sectionPhotos.length === 0 ? (
                  <div className="flex-1 flex items-center justify-center min-h-[80px] rounded-[var(--radius)] border border-dashed border-[var(--border)] bg-[var(--surface2)]">
                    <p className="text-[11px] text-[var(--text-muted)] italic">
                      {getMessage('form_recognizer.no_files')}
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-[10px] items-end">
                    {sectionPhotos.map((photo, idx) => {
                      const style = getFileTypeStyle(photo.mimeType, photo.name)
                      return (
                        <div key={photo.id} className="group relative w-[88px] h-[68px] rounded-[8px] overflow-hidden border border-[var(--border)] flex-shrink-0 flex flex-col items-center gap-[4px] ">
                          {/* Icon — opens lightbox */}
                          <button
                            type="button"
                            onClick={() => setLightboxIndex(idx)}
                            title={getMessage('photo_strip.tooltip_click_view')}
                            className="w-full h-full block cursor-zoom-in"
                          >
                            <div className="w-full h-full flex flex-col items-center justify-center gap-[3px]" style={{ backgroundColor: style.bgColor }}>
                              <style.Icon className={cn('w-[22px] h-[22px]', style.iconColor)} strokeWidth={1.5} />
                              <span className="text-[8px] text-[var(--text-muted)] px-1 text-center leading-tight">
                                {photo.name ? photo.name.substring(0, 12) : style.label}
                              </span>
                            </div>
                          </button>

                          {/* Remove */}
                          {!isSubmitted && (
                            <button
                              type="button"
                              onClick={() => handleRemoveClick(photo.id)}
                              title={getMessage('form_recognizer.tooltip_remove')}
                              className={cn(
                                'absolute top-[3px] right-[3px] z-10',
                                'w-[17px] h-[17px] rounded-full',
                                'bg-[rgba(220,38,38,0.82)] text-white text-[9px]',
                                'flex items-center justify-center cursor-pointer',
                                'hover:bg-[var(--error)] transition-[background] duration-150'
                              )}
                            >
                              ✕
                            </button>
                          )}

                          {/* Scan results bar */}
                          {photoResults[photo.id]?.length > 0 && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setViewPhotoId(photo.id) }}
                              className={cn(
                                'absolute bottom-0 left-0 right-0 z-10 h-[17px]',
                                'flex items-center justify-between px-[6px]',
                                'bg-black/30 backdrop-blur-[2px]',
                                'hover:bg-black/50 transition-colors duration-150 cursor-pointer'
                              )}
                              title={getMessage('photo_strip.tooltip_view_ocr')}
                            >
                              <div className="flex items-center gap-[3px]">
                                <ScanText size={8} strokeWidth={2.5} className="text-blue-300" />
                                <span className="text-[7px] font-bold truncate text-blue-200">
                                  {getMessage('photo_strip.label_scan_results')}
                                </span>
                              </div>
                              <span className="text-[7px] text-white/80">View</span>
                            </button>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        disabled={isSubmitted}
        onChange={handleFileChange}
      />

      {/* OCR Results modal */}
      <AiProcessModal
        open={ocrModalOpen}
        title={getMessage('ocr.modal.ocr_results_title')}
        applyLabel={getMessage('ai_modal.button_apply')}
        fields={ocrFields}
        readOnly={isSubmitted}
        onCancel={() => {
          if (pendingPhotoIdRef.current) {
            // File was just uploaded to blob — force immediate delete even with a saved draft.
            removePhoto(pendingPhotoIdRef.current, { forceDelete: true })
            pendingPhotoIdRef.current = undefined
          }
          setOcrModalOpen(false)
          setOcrMappedFields([])
          setSelectedVehicleType('')
        }}
        onContinue={async () => {
          const appliedPhotoId = pendingPhotoIdRef.current

          // Apply-time VT mismatch guard (Scenario 3) — safety net for any
          // selectedVehicleType that still differs from the current form value
          const vtFormAtApply = (useValuationStore.getState().formValues['basic-information'] as Record<string, string>)?.['vehicleType'] ?? ''
          if (
            selectedVehicleType &&
            vtFormAtApply &&
            vtFormAtApply !== 'Select' &&
            selectedVehicleType.toLowerCase() !== vtFormAtApply.toLowerCase()
          ) {
            if (appliedPhotoId) {
              await removePhoto(appliedPhotoId, { forceDelete: true })
              removePhotoLocal(appliedPhotoId) // Guarantee local removal if blob delete failed
            }
            pendingPhotoIdRef.current = undefined
            setOcrModalOpen(false)
            setOcrMappedFields([])
            setSelectedVehicleType('')
            setVtMismatchInfo({ currentValue: vtFormAtApply, responseValue: selectedVehicleType })
            return
          }

          if (ocrMappedFields.length === 0 && appliedPhotoId) {
            removePhotoLocal(appliedPhotoId)
          }
          pendingPhotoIdRef.current = undefined
          // When table fields exist, prepend the selected vehicleType as a mapped field
          const fieldsToApply = ocrMappedFields.length > 0
            ? [
                {
                  fieldKey: 'vehicleType',
                  label: FIELD_LABEL_MAP['vehicleType'] || 'Vehicle Type',
                  sectionId: 'basic-information',
                  value: selectedVehicleType,
                  inputType: 'select' as const,
                  confidence: 1,
                  source: getMessage('ocr.source_label'),
                },
                ...ocrMappedFields,
              ]
            : ocrMappedFields
          const vinFieldBeforeApply = fieldsToApply.find(f => f.fieldKey === 'vin' && f.value)
          const previousVinValue = vinFieldBeforeApply
            ? (useValuationStore.getState().formValues[vinFieldBeforeApply.sectionId] as Record<string, string> | undefined)?.['vin']
            : undefined
          if (fieldsToApply.length > 0) {
            setOcrResult(SECTION_ID, fieldsToApply)
            if (appliedPhotoId) setPhotoResult(appliedPhotoId, fieldsToApply)
          }
          applyAIFields(fieldsToApply, true)
          expandBasicIfNoneOpen()
          setOcrModalOpen(false)
          const snapshot = ocrMappedFields
          setOcrMappedFields([])
          setSelectedVehicleType('')
          const vinField = snapshot.find(f => f.fieldKey === 'vin' && f.value)
          const vinValueChanged = vinField && vinField.value.trim().toUpperCase() !== (previousVinValue ?? '').trim().toUpperCase()
          if (vinField && vinValueChanged && !FIELD_VALIDATORS.vin(vinField.value)) {
            setVinDecodeSnapshot(snapshot)
            setVinDecodeConfirmOpen(true)
          }
        }}
      />

      <ConfirmDialog
        open={vinDecodeConfirmOpen}
        title={getMessage('vin.confirm.title')}
        message={getMessage('vin.confirm.body')}
        confirmLabel={getMessage('vin.confirm.confirm')}
        cancelLabel={getMessage('vin.confirm.cancel')}
        onConfirm={() => {
          setVinDecodeConfirmOpen(false)
          setVinDecoding(true)
          handleVinDecode(vinDecodeSnapshot).finally(() => setVinDecoding(false))
          setVinDecodeSnapshot([])
        }}
        onCancel={() => {
          setVinDecodeConfirmOpen(false)
          setVinDecodeSnapshot([])
        }}
      />

      {/* VIN Decode results modal */}
      <AiProcessModal
        open={vinModalOpen}
        title={getMessage('ocr.modal.vin_results_title')}
        fields={toModalFields(vinMappedFields)}
        hideConfidence
        readOnly={isSubmitted}
        onContinue={() => {
          setOcrResult(SECTION_ID, vinMappedFields)
          applyAIFields(vinMappedFields, true)
          setVinModalOpen(false)
          setVinMappedFields([])
        }}
        onCancel={() => {
          setVinModalOpen(false)
          setVinMappedFields([])
        }}
      />

      <AiProcessModal
        open={removeModalOpen}
        title={getMessage(removeModalFields.length > 0 ? 'photo.confirm.remove_document_title' : 'photo.confirm.remove_document_label')}
        applyLabel={getMessage(removeModalFields.length > 0 ? 'photo.confirm.remove_document_title' : 'photo.confirm.remove_document_label')}
        fields={removeModalFields}
        warningKey={removeModalFields.length > 0 ? 'photo.confirm.remove_document_warning' : undefined}
        infoKey={removeModalHasRetainedFields ? 'photo.confirm.remove_retained_fields' : undefined}
        emptyMessageKey="photo.confirm.remove_no_fields_message"
        showApplyWhenEmpty
        hideConfidence
        onContinue={() => {
          // Clear only the specific matched fields (excludes vehicleType / use / type)
          if (removeModalMappedFields.length > 0) {
            clearSpecificFields(
              removeModalMappedFields
                .filter((f) => f.sectionId && f.fieldKey)
                .map((f) => ({ sectionId: f.sectionId, fieldKey: f.fieldKey }))
            )
          }
          // Promote confidence badges for backed fields to the best score from remaining images.
          if (removeModalConfidenceUpdates.length > 0) {
            promoteFieldConfidence(removeModalConfidenceUpdates)
          }
          removePhoto(removeModalPhotoId!)
          if (sectionPhotos.filter(p => p.id !== removeModalPhotoId).length === 0) {
            clearSectionOcrData(SECTION_ID)
          }
          setRemoveModalOpen(false)
          setRemoveModalPhotoId(null)
          setRemoveModalFields([])
          setRemoveModalMappedFields([])
          setRemoveModalHasRetainedFields(false)
          setRemoveModalConfidenceUpdates([])
        }}
        onCancel={() => {
          setRemoveModalOpen(false)
          setRemoveModalPhotoId(null)
          setRemoveModalFields([])
          setRemoveModalMappedFields([])
          setRemoveModalHasRetainedFields(false)
          setRemoveModalConfidenceUpdates([])
        }}
      />

      <ConfirmDialog
        open={!!vtMismatchInfo}
        title={getMessage('photo.confirm.vehicle_type_mismatch_title')}
        message={getMessage('photo.confirm.vehicle_type_mismatch_warning', vtMismatchInfo ?? {})}
        confirmLabel={getMessage('common.button.ok')}
        hideCancel
        onConfirm={() => {
          // pendingPhotoIdRef.current is now set in ALL mismatch paths (upload-time,
          // VT-selector change, Apply-time). removePhoto may leave the photo in local
          // state if the blob API call fails — removePhotoLocal guarantees UI removal.
          const pendingId = pendingPhotoIdRef.current
          if (pendingId) {
            removePhoto(pendingId, { forceDelete: true })
            removePhotoLocal(pendingId) // Guaranteed local/IDB removal as fallback
            pendingPhotoIdRef.current = undefined
          }
          setOcrModalOpen(false)
          setOcrFields([])
          setOcrMappedFields([])
          setSelectedVehicleType('')
          setVtMismatchInfo(null)
        }}
        onCancel={() => {
          const pendingId = pendingPhotoIdRef.current
          if (pendingId) {
            removePhoto(pendingId, { forceDelete: true })
            removePhotoLocal(pendingId)
            pendingPhotoIdRef.current = undefined
          }
          setOcrModalOpen(false)
          setOcrFields([])
          setOcrMappedFields([])
          setSelectedVehicleType('')
          setVtMismatchInfo(null)
        }}
      />

      <ConfirmDialog
        open={unsupportedExt !== null}
        title={getMessage('ocr.confirm.unsupported_title')}
        message={getMessage('ocr.confirm.unsupported_body', { ext: unsupportedExt ?? '' })}
        confirmLabel={getMessage('common.button.ok')}
        hideCancel
        onConfirm={() => setUnsupportedExt(null)}
        onCancel={() => setUnsupportedExt(null)}
      />

      {/* View scan results modal */}
      <AiProcessModal
        open={viewPhotoId !== null}
        title={getMessage('form_recognizer.section_title')}
        fields={toModalFields(photoResults[viewPhotoId ?? ''] ?? [], getMessage('ocr.source_label'))}
        readOnly={isSubmitted}
        onContinue={() => {
          applyAIFields(photoResults[viewPhotoId ?? ''] ?? [], true)
          setViewPhotoId(null)
        }}
        onCancel={() => setViewPhotoId(null)}
      />

      {/* Valuation Report Summary modal */}
      <AiProcessModal
        open={viewSummaryOpen}
        title={getMessage('form_recognizer.modal_summary_title')}
        fields={toModalFields(valuationReportFields, getMessage('ocr.source_label'))}
        readOnly={isSubmitted}
        onContinue={() => {
          applyAIFields(valuationReportFields, true)
          setViewSummaryOpen(false)
        }}
        onCancel={() => setViewSummaryOpen(false)}
      />

      {/* Full-screen OCR processing overlay */}
      {ocrLoading && (
        <div className="fixed inset-0 z-[800] bg-black/40 backdrop-blur-[2px] flex items-center justify-center px-4">
          <div className={cn(
            'flex items-center gap-3 px-4 sm:px-6 py-3 sm:py-4 shadow-2xl w-full max-w-[320px]',
            'bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)]'
          )}>
            <Loader2 size={22} className="text-[var(--accent)] animate-spin flex-shrink-0" />
            <div>
              <p className="text-[13px] font-bold text-[var(--text)]">
                {vinDecoding ? getMessage('vin.info.loading') : getMessage('form_recognizer.processing_title')}
              </p>
              <p className="text-[11px] text-[var(--text-muted)]">
                {vinDecoding ? getMessage('vin.info.loading_subtitle') : getMessage('form_recognizer.processing_subtitle')}
              </p>
            </div>
          </div>
        </div>
      )}

      {lightboxIndex !== null && (
        <PhotoLightbox
          photos={sectionPhotos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  )
}
